import { useApolloClient } from "@apollo/client";
import { LabeledValue } from "@msys/ui";
import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../../auth/useUserData.js";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import { AutoSave } from "../../../commons/form-fields/AutoSave.js";
import { SwitchField } from "../../../commons/form-fields/SwitchField.js";
import { SectionCard } from "../sections/SectionCard.js";
import {
  OrganisationDetailsQualificationsBox_ViewerOrganisationFragment,
  useOrganisationDetailsQualificationsBox_ModifyOrganisationMutation,
} from "./OrganisationDetailsQualificationsBox.generated.js";

interface Props {
  organisation: OrganisationDetailsQualificationsBox_ViewerOrganisationFragment;
}

interface QualificationsFormValues {
  isMeister: boolean;
}

function QualificationsForm({ organisation }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();
  const { t } = useTranslate(["OrganisationProfile"]);

  const [modifyOrganisation] =
    useOrganisationDetailsQualificationsBox_ModifyOrganisationMutation({
      client,
      refetchQueries: [namedOperations.Query.OrganisationDetails],
    });

  const initialValues: QualificationsFormValues = React.useMemo(
    () => ({
      isMeister: organisation?.isMeister ?? false,
    }),
    [organisation?.isMeister]
  );

  const validationSchema = Yup.object().shape({
    isMeister: Yup.boolean().required(),
  });

  const handleSubmit = async (values: QualificationsFormValues) => {
    try {
      const input = {
        organisationId: organisation.id,
        isMeister: values.isMeister,
      };

      await modifyOrganisation({
        variables: { input },
      });
    } catch (error) {
      if (error instanceof Error) {
        enqueueSnackbar(error.message, { variant: "error" });
      }
    }
  };

  // if (organisation?.isMeister === undefined) return null;

  return (
    <Formik<QualificationsFormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      <Form>
        <Stack spacing={1}>
          <SwitchField
            name="isMeister"
            label={t("Meister Qualifications", { ns: "OrganisationProfile" })}
            disabled={false}
          />
          <AutoSave />
        </Stack>
      </Form>
    </Formik>
  );
}

function QualificationsView({ organisation }: Props) {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);

  return (
    <LabeledValue
      label={t("Meister Qualifications", { ns: "OrganisationProfile" })}
    >
      {organisation?.isMeister
        ? t("Yes", { ns: "Global" })
        : t("No", { ns: "Global" })}
    </LabeledValue>
  );
}

export function OrganisationDetailsQualificationsBox({ organisation }: Props) {
  const { t } = useTranslate(["OrganisationProfile"]);
  const viewer = useUserData().currentUser!;

  return (
    <SectionCard
      title={t("Qualifications & Awards", { ns: "OrganisationProfile" })}
    >
      <RestrictedByOrganisationPermissionWithDebug
        permission="organisation_profile_update"
        otherwise={<QualificationsView organisation={organisation} />}
      >
        {viewer.organisation.id === organisation.id &&
        !!viewer.roles.find(r => r.internalName === "ORG_ADMIN") ? (
          <QualificationsForm organisation={organisation} />
        ) : (
          <QualificationsView organisation={organisation} />
        )}
      </RestrictedByOrganisationPermissionWithDebug>
    </SectionCard>
  );
}
